import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { CssSelectors } from '../../../../web/styles/styles';
import {
	CELL_SIDE_PADDING,
	TICKET_NUMBER_MARKER_GAP,
	TICKET_NUMBER_MARKER_SLOT_WIDTH,
	TICKET_TABLE_COLUMN_WIDTHS,
	TICKET_TABLE_MIN_WIDTH_WITH_ACCOUNT,
	TICKET_TABLE_MIN_WIDTH_WITHOUT_ACCOUNT,
} from '../ticketTableColumns';

/**
 * Styles for the inbox's table layout — a databoard-style DataGrid of tickets with filter menus on
 * the column headers. Header typography mirrors the DataGrid's HEADER_CELL_TEXT so interactive
 * headers read like the static ones.
 */

// Row hover wash, matching the desk list rows (see inboxStyles).
const ROW_HOVER = THEME.colors.background.main;

/**
 * Pins every child of a flex cell at its natural width. Status, priority and the channel badge come
 * from fixed vocabularies and their columns are budgeted to fit the longest one, so a sub-pixel
 * shortfall must not be allowed to clip one of them mid-word.
 */
const KEEP_CHILDREN_WHOLE = CssSelectors.allChildren({ flexShrink: 0 });

/**
 * The Button size presets pad their sides whenever the button has an svg child — here that child is
 * always the menu chevron, and the padding would both indent the value away from its column edge
 * and steal ~24px from the value itself.
 */
const CLEAR_BUTTON_SIDE_PADDING = { ':has(> svg)': { paddingLeft: 0, paddingRight: 0 } };

export const ticketTableStyles = StyleSheet.create({
	tablePane: {
		height: '100%',
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
	},
	// Anchors the bulk-actions overlay so selecting rows never shifts the header/table below.
	tableHeaderArea: {
		flex: 'none',
		position: 'relative',
	},
	viewsRow: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 4,
		padding: '12px 20px',
	},
	viewTab: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 7,
		height: 'auto',
		padding: '6px 11px',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		':hover': { backgroundColor: ROW_HOVER },
	},
	viewTabActive: {
		backgroundColor: THEME.colors.primary.light,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	viewTabIcon: {
		width: 16,
		height: 16,
		flex: 'none',
		color: THEME.colors.text.light,
	},
	viewTabCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	tableScroll: {
		flex: 1,
		minHeight: 0,
		overflow: 'auto',
	},
	fixedTable: {
		tableLayout: 'fixed',
	},
	fixedTableWithAccount: {
		minWidth: TICKET_TABLE_MIN_WIDTH_WITH_ACCOUNT,
	},
	fixedTableWithoutAccount: {
		minWidth: TICKET_TABLE_MIN_WIDTH_WITHOUT_ACCOUNT,
	},
	colCheckbox: {
		width: TICKET_TABLE_COLUMN_WIDTHS.select,
	},
	colNumber: {
		width: TICKET_TABLE_COLUMN_WIDTHS.number,
	},
	// Auto, so the subject takes every pixel the budgeted columns leave over; its floor lives in
	// TICKET_TABLE_COLUMN_WIDTHS, held by the table's min-width.
	colSubject: {
		width: 'auto',
	},
	colContact: {
		width: TICKET_TABLE_COLUMN_WIDTHS.contact,
	},
	colAccount: {
		width: TICKET_TABLE_COLUMN_WIDTHS.account,
	},
	colStatus: {
		width: TICKET_TABLE_COLUMN_WIDTHS.status,
	},
	colPriority: {
		width: TICKET_TABLE_COLUMN_WIDTHS.priority,
	},
	colCategory: {
		width: TICKET_TABLE_COLUMN_WIDTHS.category,
	},
	colAssignee: {
		width: TICKET_TABLE_COLUMN_WIDTHS.assignee,
	},
	colTime: {
		width: TICKET_TABLE_COLUMN_WIDTHS.lastUpdate,
	},
	// Interactive header control (filter menu trigger / sort toggle) inside a <th>. Font metrics
	// copy DataGrid's HEADER_CELL_TEXT so the header row stays visually uniform.
	headerBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		height: 'auto',
		// The column is budgeted, the active value is not: an assignee is a person's name. Hold the
		// button inside its header cell and let the value below ellipsize rather than run onto the
		// next column's heading.
		maxWidth: '100%',
		minWidth: 0,
		whiteSpace: 'nowrap',
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontWeight: 600,
		fontSize: 12,
		letterSpacing: '1.1px',
		textTransform: 'uppercase',
		color: 'inherit',
		...CLEAR_BUTTON_SIDE_PADDING,
		':hover': { color: THEME.colors.primary.main, background: 'transparent' },
		':disabled': { cursor: 'default', color: 'inherit' },
	},
	headerBtnActive: {
		color: THEME.colors.primary.main,
	},
	// The active value stands in for the column's own heading, so it gets the whole cell: it is user
	// text — a contact's email, an account or a person's name — and keeps its natural casing against
	// the uppercase header treatment. `minWidth: 0` is what lets it ellipsize at the column's edge: a
	// flex item defaults to `min-width: auto` and refuses to shrink below its own text, which is what
	// pushed the assignee's name over the next column.
	headerBtnValue: {
		...THEME.common.truncate,
		minWidth: 0,
		textTransform: 'none',
		letterSpacing: 'normal',
	},
	// Never the item that gives up room — a squashed chevron reads as a rendering fault.
	headerBtnChevron: {
		flex: 'none',
	},
	contactFilterPopover: {
		width: 260,
	},
	contactFilterPanel: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		paddingTop: 6,
		paddingBottom: 6,
	},
	contactFilterClearBtn: {
		alignSelf: 'flex-start',
		height: 'auto',
		paddingTop: THEME.spacing.xs,
		paddingBottom: THEME.spacing.xs,
		paddingLeft: THEME.spacing.sm,
		paddingRight: THEME.spacing.sm,
		border: 'none',
		background: 'transparent',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { background: 'transparent', color: THEME.colors.text.main },
	},
	headerChevronAsc: {
		transform: 'rotate(180deg)',
	},
	filterMenu: {
		// Sized to the widest row, not to a fixed 170: a row is label + a right-aligned hint, and the
		// popup's overflow-x is hidden, so anything past the pinned width was silently cut off.
		width: 'max-content',
		minWidth: 170,
		maxWidth: 320,
		padding: 5,
		display: 'flex',
		flexDirection: 'column',
		gap: 1,
	},
	// The hint sits at the far right of the row, so a column of counts reads down the menu.
	menuItemHint: {
		marginLeft: 'auto',
		paddingLeft: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		fontVariantNumeric: 'tabular-nums',
	},
	filterMenuItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		width: '100%',
		height: 'auto',
		padding: '7px 10px',
		border: 'none',
		borderRadius: 7,
		background: 'transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		textAlign: 'left',
		':hover': { backgroundColor: ROW_HOVER },
	},
	// TicketSearchBar host overrides: inline at the right of the views row, without the desk bar's
	// framing. zIndex auto so the bulk-selection overlay covers it like the rest of the row.
	tableSearchBar: {
		marginLeft: 'auto',
		width: 300,
		minWidth: 200,
		padding: 0,
		borderBottom: 'none',
		zIndex: 'auto',
	},
	// Right-anchor the Search criteria popover under its toggle instead of spanning the bar.
	tableFilterPanel: {
		left: 'auto',
		right: 0,
		top: 'calc(100% + 6px)',
		width: 320,
	},
	searchCriteriaRow: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		padding: '0 20px 10px',
	},
	searchCriteriaText: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	clearFiltersBtn: {
		height: 'auto',
		padding: '4px 8px',
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main, background: 'transparent' },
	},
	accountScope: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		minWidth: 0,
	},
	accountScopeEditing: {
		width: 320,
	},
	accountScopeButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 7,
		minWidth: 0,
		maxWidth: 300,
		height: 32,
		padding: '0 10px',
	},
	accountScopePrefix: {
		flex: 'none',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
	},
	accountScopeName: {
		...THEME.common.truncate,
		minWidth: 0,
	},
	accountScopeClearBtn: {
		height: 32,
		padding: '0 8px',
		border: 'none',
		background: 'transparent',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { background: 'transparent', color: THEME.colors.text.main },
	},
	accountComboboxInput: {
		width: '100%',
		height: 32,
	},
	accountComboboxContent: {
		width: 320,
	},
	// Overlays the views row while rows are selected (Gmail-style) so the table never jumps.
	selectionBar: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 10,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '0 20px',
		backgroundColor: THEME.colors.primary.light,
	},
	selectionCount: {
		marginRight: 4,
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	bulkMenuBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		height: 'auto',
		padding: '4px 10px',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		':hover': { color: THEME.colors.primary.main, background: THEME.colors.background.light },
		':disabled': { cursor: 'default', opacity: 0.6 },
	},
	// The shortcut key beside the label, so the keys are learned from the bar rather than from a doc.
	bulkMenuKey: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 16,
		padding: '1px 4px',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 4,
		backgroundColor: THEME.colors.background.light,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		lineHeight: 1.4,
	},
	selectionClearBtn: {
		marginLeft: 'auto',
		height: 'auto',
		padding: '4px 8px',
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main, background: 'transparent' },
	},
	actionError: {
		flex: 'none',
		padding: '6px 20px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.dark,
	},
	row: {
		cursor: 'pointer',
		':hover': { backgroundColor: ROW_HOVER },
	},
	rowActive: {
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	rowDimmed: {
		opacity: 0.6,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
	},
	cellCheckbox: {
		whiteSpace: 'nowrap',
	},
	cellStopClick: {
		display: 'inline-flex',
		alignItems: 'center',
		minWidth: 0,
		maxWidth: '100%',
	},
	// A wider gap than the pills' own so the channel badge reads as a separate value, not as part of
	// the status menu whose chevron it sits beside.
	statusPills: {
		gap: THEME.spacing.sm,
		...KEEP_CHILDREN_WHOLE,
	},
	cellTruncate: {
		...THEME.common.truncate,
		minWidth: 0,
	},
	// A pill or menu, sized by its own content. Clipped rather than ellipsised, since the ellipsis
	// belongs inside the pill's label, and only as a last resort — the column is sized to avoid it.
	cellControl: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	truncatedText: {
		...THEME.common.truncate,
		display: 'block',
		minWidth: 0,
	},
	cellEditBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		maxWidth: '100%',
		minWidth: 0,
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		font: 'inherit',
		color: 'inherit',
		textAlign: 'left',
		...CLEAR_BUTTON_SIDE_PADDING,
		':hover': { background: 'transparent', color: THEME.colors.primary.main },
	},
	cellEditBtnFixed: {
		...KEEP_CHILDREN_WHOLE,
	},
	/**
	 * Matches the shared tag Pill's geometry and type so the priority column reads as one set of
	 * controls — only the fill and the label colour differ. Keep in step with `pillStyles`/`tagStyles`
	 * in components-v2/Pill if that geometry ever moves.
	 */
	urgentPriorityPill: {
		display: 'flex',
		alignItems: 'center',
		width: 'fit-content',
		padding: `${THEME.spacing.xs}px ${THEME.spacing.md}px`,
		borderRadius: 18,
		backgroundColor: THEME.colors.danger.dark,
		color: THEME.colors.danger.contrastText,
		fontFamily: 'Sora',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		whiteSpace: 'nowrap',
	},
	cellEditChevron: {
		flex: 'none',
		color: THEME.colors.text.light,
	},
	// Indented past the marker slot, so the heading sits over the numbers rather than over the markers.
	numberHeader: {
		textAlign: 'left',
		paddingLeft: CELL_SIDE_PADDING + TICKET_NUMBER_MARKER_SLOT_WIDTH + TICKET_NUMBER_MARKER_GAP,
	},
	// Truncated, not just aligned — an over-budget number overflows onto the subject otherwise.
	cellNumber: {
		...THEME.common.truncate,
		color: THEME.colors.text.light,
	},
	// Packed together: pinning one to each edge turned the column's spare width into a gap.
	numberWithMarker: {
		display: 'flex',
		alignItems: 'center',
		gap: TICKET_NUMBER_MARKER_GAP,
		minWidth: 0,
	},
	// Held open on every row, so the numbers share an x and the markers scan as their own column.
	gitHubMarkerSlot: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flex: 'none',
		width: TICKET_NUMBER_MARKER_SLOT_WIDTH,
	},
	numberValue: {
		...THEME.common.truncate,
		minWidth: 0,
	},
	gitHubMarker: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flex: 'none',
	},
	gitHubMarkerLink: {
		borderRadius: THEME.common.borderRadius,
		':hover': { opacity: 0.75 },
	},
	gitHubMarkerGlyph: {
		display: 'block',
	},
	// Yellow is the triage repo and blue is engineering, the two stages the desk asked to tell apart
	// from the list. Both markers also state the stage in their tooltip and accessible name.
	gitHubMarkerTriage: {
		color: THEME.colors.info.dark,
	},
	gitHubMarkerEngineering: {
		color: THEME.colors.primary.main,
	},
	gitHubMarkerGeneric: {
		color: THEME.colors.text.light,
	},
	gitHubMarkerClosed: {
		color: THEME.colors.text.light,
		opacity: 0.5,
	},
	subjectBtn: {
		...THEME.common.truncate,
		display: 'block',
		maxWidth: '100%',
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		font: 'inherit',
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		textAlign: 'left',
		':hover': { background: 'transparent', textDecoration: 'underline' },
	},
	subjectBtnActive: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	subjectBtnDimmed: {
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
	},
	cellMuted: {
		color: THEME.colors.text.light,
	},
	cellTime: {
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
	},
	tableState: {
		padding: 32,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
});
